import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import BottomDrawer from "../../common/drawer/bottomDrawer";
import {
  useCommonFetch,
  usePageFilter,
  useFilterOptions,
} from "../../../../hooks";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonForm from "../../../components/form/form";
import toast from "react-hot-toast";
import { convertToLabelValue } from "../../../../utils/common";
import {
  commonConditions,
  createActionButtons,
} from "../../../../utils/actionButtonHelpers";
import {
  createTeam,
  updateTeam,
  getTeamList,
  deleteTeam,
} from "../../../../services/workspace/team.service";
import { getUserList } from "../../../../services/workspace/user.service";
import { fetchStaffList } from "../../../../store/slices/user.slice";
import { useDispatch, useSelector } from "react-redux";
import { usePermissionCheck } from "../../../../hooks/usePermissionCheck";

export const actions_type = {
  DELETE: "DELETE",
  ADD_EDIT: "ADD_EDIT",
};

const TableHeaderData = [
  {
    label: "Team Name",
    key: "name",
  },
  {
    label: "Department",
    key: "department",
  },
  {
    label: "Manager",
    key: "manager",
  },
  {
    label: "Team Lead",
    key: "team_lead",
  },
  {
    label: "Team Members",
    key: "members",
  },
  {
    label: "Created At",
    key: "created_at",
  },
];

export const items = [{ label: "Manage Team", path: "" }];

const ManageTeam = () => {
  const navigate = useNavigate();
  const [isEditValue, setIsEditValue] = useState(null);
 
  const dispatch = useDispatch();
  
  // Check department permission - only fetch if user has permission
  const departmentPermissions = usePermissionCheck("configuration", "department");
  const hasDepartmentPermission = departmentPermissions.canView || departmentPermissions.hasAnyPermission;
  
  // ✨ Auto fetch and cache department options from Redux ONLY if user has permission!
  // No more repeated API calls - data fetched once and stored!
  const { departments, departmentsLoading, refresh } = useFilterOptions(
    hasDepartmentPermission ? ["departments"] : [], // Only fetch if permission exists
    { autoFetch: hasDepartmentPermission } // Only auto-fetch if permission exists
  );
  const { staffList, staffLoading, role: userType, staffError } = useSelector((state) => state.user);

  // staffList is already in {label, value} format from Redux (converted by convertToLabelValue in user.slice.js)
  // So we can use it directly, but ensure it's an array and has the correct structure
  const staffOptions = Array.isArray(staffList) && staffList.length > 0
    ? staffList.map((item) => ({
        label: item?.label || item?.name || "-",
        value: item?.value || item?.id || item?.uuid,
      })).filter(item => item.value) // Filter out items without valid values
    : [];

  // Fetch staff/users for team lead and members selection
  // const fetchStaffList = (search = "") => {
  //   setStaffLoading(true);
  //   getUserList({
  //     user_type: "staff",
  //     search: search,
  //     limit: 100,
  //   })
  //     .then((response) => {
  //       const staffData = response?.user?.data || [];
  //       const formattedStaff = convertToLabelValue(staffData, true);
  //       setStaffList(formattedStaff || []);
  //     })
  //     .catch((error) => {
  //       toast.error("Failed to fetch staff data!");
  //       console.log(error);
  //     })
  //     .finally(() => {
  //       setStaffLoading(false);
  //     });
  // };

  // Fetch data with useCommonFetch
  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
    applyFilters,
    clearFilters,
  } = useCommonFetch(getTeamList, {
    dataPath: "team.data",
    totalPath: "team.meta.total",
    errorMessage: "Failed to fetch team data!",
    debounceDelay: 500,
    filterModule: "team", // Add filter module name for Redux persistence
    customParams: {
      with: "department,leader,manager",
    },
    sortingOptions: [
      {
        label: "Date",
        field: "created_at",
        buttonText: { ascending: "Oldest First", descending: "Newest First" },
      },
      {
        label: "Name",
        field: "name",
        buttonText: { ascending: "A-Z", descending: "Z-A" },
      },
    ],
    sortingModule: "team",
    orderParamNames: {
      orderByKey: "order_by",
      orderTypeKey: "order_type",
      includeDisabledKey: "includeDisabled",
    },
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,
    // Add data transformer function
    dataTransformer: (data) => {
      if (process.env.NODE_ENV === 'development') {
      }
      if (Array.isArray(data)) {
        const transformedData = data.map((item) => ({
          ...item,
          id: {
            data: item.uuid,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
          name: {
            data: item.name,
            className: "",
            sticky: false,
            variant: "name",
            customCases: null,
          },
          department: {
            data: item.department?.name || "-",
            className: "",
            sticky: false,
            variant: "select",
            options: departments,
            customCases: null,
          },
          manager: {
            data: item?.manager?.user?.name,
            className: "",
            sticky: false,
            variant: "text",
            customCases: (manager) => {
              if (!manager) return "No manager";
              // Handle if manager is an object with user property
              if (typeof manager === "object" && manager.user) {
                return manager.user.name || "No manager";
              }
              if (typeof manager === "object" && manager.name) return manager.name;
              if (typeof manager === "string") return manager;
              return "No manager";
            },
          },
          team_lead: {
            data: item?.leader?.user?.name            ,
            className: "",
            sticky: false,
            variant: "text",
            customCases: (lead) => {
              if (!lead) return "No team lead";
              // Handle if leader is an object with user property
              if (typeof lead === "object" && lead.user) {
                return lead.user.name || "No team lead";
              }
              if (typeof lead === "object" && lead.name) return lead.name;
              if (typeof lead === "string") return lead;
              return "No team lead";
            },
          },
          members: {
            data: item?.team_members            ,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
          // members: {
          //   // Process the data to always return a string/number, not an object
          //   data: (() => {
          //     // If total_members exists as a number, use it
          //     if (typeof item.total_members === 'number') {
          //       return item.total_members;
          //     }
          //     // If members is an array, return the length
          //     if (Array.isArray(item.members)) {
          //       return item.members.length;
          //     }
          //     // Default to 0
          //     return 0;
          //   })(),
          //   className: "",
          //   sticky: false,
          //   variant: "text",
          //   customCases: (count) => {
          //     // count is now always a number
          //     if (typeof count === 'number') {
          //       return `${count} member${count !== 1 ? "s" : ""}`;
          //     }
          //     return "0 members";
          //   },
          // },
          created_at: {
            data: item.created_at,
            className: "",
            sticky: false,
            variant: "date-time",
            customCases: null,
          },
          status: {
            data: item.status ? 1 : 0,
            className: "",
            sticky: false,
            variant: "status",
            customCases: null,
          },
          actions: {
            data: item.id,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
        }));

        const actionButton = createActionButtons(
          {
            view: {
              handler: (item) => {
                navigate(`/configuration/manage-team/detail/${item.id.data}`);
              },
              // Show view button only for Super Admin (0) and Workspace Admin (1)
              show: () => userType === 0 || userType === 1,
            },
            delete: {
              handler: (item) => {
                handleDelete(item.id.data, deleteTeam, {
                  successMessage: "Team deleted successfully!",
                  errorMessage: "Failed to delete team!",
                });
              },
              show: commonConditions.nonSystemGenerated,
            },
            // edit: {
            //   handler: (item) => {
            //     openModal(item, actions_type.ADD_EDIT);
                
            //     // Refresh staff list to ensure we have latest data
            //     dispatch(fetchStaffList({ type: 2, additional_info: true }));
                
            //     // The item passed here is the transformed item, but original data is preserved via spread operator
            //     // Access the original data from the item
            //     // Note: item has both transformed fields (like item.department.data) and original fields (item.department)
                
            //     // Get department ID - try multiple ways to access
            //     const departmentId = item.department?.id || 
            //                        item.department?.data?.id || 
            //                        item.department_id || 
            //                        null;
                
            //     // Get leader ID - try multiple ways to access
            //     const leaderData = item.leader || item.team_lead?.data;
            //     const teamLeadId = leaderData?.user?.id || 
            //                      leaderData?.id || 
            //                      null;
                
            //     // Get members - try multiple ways to access
            //     const originalMembers = item.members || item.members?.data || [];
            //     const membersIds = Array.isArray(originalMembers) && originalMembers.length > 0
            //       ? originalMembers.map((member) => {
            //           // Extract member ID from various possible structures
            //           return member.user?.id || member.id || member;
            //         }).filter(id => id != null) // Remove null/undefined
            //       : [];
                
            //     const InitialData = {
            //       id: item.id?.data || item.uuid || item.id,
            //       name: item.name?.data || item.name || "",
            //       department_id: departmentId, // ID value for form
            //       leader_id: teamLeadId, // ID value for form
            //       members: membersIds, // Array of ID values for form
            //     };
                
            //     console.log("=== EDIT HANDLER DEBUG ===");
            //     console.log("item:", item);
            //     console.log("InitialData:", InitialData);
            //     console.log("departments:", departments);
            //     console.log("staffOptions length:", staffOptions?.length);
            //     console.log("========================");
                
            //     setIsEditValue(InitialData);
            //   },
            //   show: commonConditions.always, // Show edit button for all items
            // },
          },
          {
            systemGeneratedBadge: true,
            badgeText: "System Generated",
            badgeVariant: "info",
          }
        );

        return {
          data: transformedData,
          action: actionButton,
        };
      }
      return { data: [], action: {} };
    },
  });

  // ✨ Single hook for ALL filter setup - define everything ONCE!
  const { filterProps } = usePageFilter(
    "team", // Module name (only once!)
    applyFilters, // From useCommonFetch
    clearFilters, // From useCommonFetch
    {
      filters: ["department"], // Filter names (only once!)
      options: { departmentOptions: departments }, // ✨ From Redux cache!
      onRefreshOptions: () => refresh("departments"), // ✨ Refresh callback for manual refresh!
    }
  );

  // Fetch staff list on mount (departments auto-fetched by useFilterOptions)
  useEffect(() => {
    dispatch(fetchStaffList({ type: 2, additional_info: true }));
  }, [dispatch]);

  // Comprehensive form fields
  const comprehensiveFields = [
    // Basic text fields
    {
      name: "name",
      label: "Team Name",
      type: "text",
      placeholder: "Enter team name",
      required: true,
      minLength: 2,
      maxLength: 40,
    },
    {
      name: "department_id",
      label: "Department",
      type: "select",
      options: departments, // ✨ From Redux cache
      required: true,
      disabled: departmentsLoading, // ✨ Disable jab tak load ho raha hai
      placeholder: departmentsLoading
        ? "Loading departments..."
        : "Select department",
      onRefresh: () => refresh("departments"), // ✨ Refresh button ke liye
    },
    {
      name: "manager_id",
      label: "Manager",
      type: "select",
      options: staffOptions,
      required: false,
      disabled: staffLoading,
      placeholder: staffLoading
        ? "Loading staff..."
        : "Select manager (optional)",
      onRefresh: () => dispatch(fetchStaffList({ type: 2, additional_info: true })),
      isClearable: true, // Allow clearing the selection
    },
    {
      name: "leader_id",
      label: "Team Lead",
      type: "select",
      options: staffOptions,
      required: false,
      disabled: staffLoading,
      placeholder: staffLoading
        ? "Loading staff..."
        : "Select team lead (optional)",
      onRefresh: () => dispatch(fetchStaffList({ type: 2, additional_info: true })),
      isClearable: true, // Allow clearing the selection
    },
    {
      name: "members",
      label: "Team Members",
      type: "select",
      options: staffOptions,
      required: false,
      multiple: true,
      isMulti: true, // Explicitly set isMulti
      disabled: staffLoading,
      placeholder: staffLoading
        ? "Loading staff..."
        : "Select team members",
      onRefresh: () => dispatch(fetchStaffList({ type: 2, additional_info: true })),
    },
  ];

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // Prepare data for API
    
    // Process members - filter out null/undefined and extract values properly
    let processedMemberIds = [];
    if (Array.isArray(values.members) && values.members.length > 0) {
      processedMemberIds = values.members
        .map((member) => {
          // Handle both cases: if member is an object with value property, or if it's already an ID
          if (member && typeof member === 'object' && member.value != null) {
            return member.value;
          }
          // If member is already a number/string ID
          if (member != null && member !== '') {
            return member;
          }
          return null;
        })
        .filter((id) => id != null && id !== ''); // Remove null, undefined, and empty string values
    }
    
    const submitData = {
      name: values.name,
      department_id: values.department_id?.value || values.department_id,
      // Handle manager_id - convert empty string to null
      manager_id: values.manager_id?.value || (values.manager_id && values.manager_id !== "" ? values.manager_id : null) || null,
      // Handle leader_id - convert empty string to null
      leader_id: values.leader_id?.value || (values.leader_id && values.leader_id !== "" ? values.leader_id : null) || null,
      members: processedMemberIds,
    };

    // Edit
    if (isEditValue) {
      await handleUpdate(submitData, isEditValue.id, updateTeam, {
        successMessage: "Team updated successfully!",
        errorMessage: "Failed to update team!",
      });
    } else {
      // Create new
      await handleCreate(submitData, createTeam, {
        successMessage: "Team created successfully!",
        errorMessage: "Failed to create team!",
      });
    }

    setSubmitting(false);
    // Reset form after submission
    setTimeout(() => {
      resetForm();
      setIsEditValue(null);
    }, 1000);
  };

  // Handle form cancellation
  const handleCancel = () => {

    setIsEditValue(null);
    closeModal();
    // setFormValues({});
    // setTestMode('create');
  };

  // Handle drawer close
  const handleDrawerClose = (isOpen) => {
    if (!isOpen) {
      handleCancel();
    }
  };

  return (
    <div className={"flex flex-col gap-2"}>
      {/* ✨ Show loading indicator jab departments load ho rahe hon */}
      {/* {departmentsLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-sm text-blue-800">
          🔄 Loading filter options...
        </div>
      )} */}

      <PageTableHeader
        isSearch
        onSearchChange={handleSearchChange}
        add
        onClickAdd={() => openModal("", actions_type.ADD_EDIT)}
        // filter
        shorting
        breadcrumbs={items}
        sortingModule="team"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
        // filterProps={filterProps} // ✨ Just spread filterProps - that's it!
      />

      <div className={" bg-white"}>
        <CommonTable
          search={search}
          TableHeaderData={TableHeaderData}
          tabledata={tableData.data}
          currentPage={currentPage}
          totalData={totalData}
          setCurrentPage={handlePageChange}
          loading={loading}
          action={tableData.action}
        />

        <BottomDrawer
          isOpen={action === actions_type.ADD_EDIT}
          setIsOpen={handleDrawerClose}
          title={isEditValue ? "Edit Team" : "Add Team"}
          size="500px"
          position="right"
        >
          <CommonForm
            fields={comprehensiveFields}
            initialData={isEditValue ? isEditValue : {}}
            onSubmit={handleSubmit}
            submitButtonText={isEditValue ? "Update Team" : "Create Team"}
            onCancel={handleCancel}
            cancelButtonText="Reset"
            columnsPerRow={1}
            gap="gap-4"
            rowClassName=""
            fieldClassName="bg-white p-3 rounded shadow-sm"
          />
        </BottomDrawer>
      </div>
    </div>
  );
};
export default ManageTeam;
